/**
 * @insolitum/module-events
 *
 * Browser-safe helper for publishing cross-module events from iframe-embedded
 * Next.js modules. Modules cannot use @insolitum/nats-client (raw TCP), so
 * events go over HTTPS to the websocket-gateway, which republishes them on
 * NATS as `tenant.{org}.events.{module}.{event}` (Envelope v2).
 *
 * Fire-and-forget by design: a failed publish must never break the UI.
 */

/** Default public websocket-gateway host (HTTP API + Socket.io). */
export const DEFAULT_EVENTS_URL = 'https://ws.insolitum.ai';

export interface PublishModuleEventOptions {
  /** Module slug, e.g. 'agenda', 'provisio', 'lexshift'. */
  module: string;
  /** Event name, e.g. 'task_completed', 'order_delivered'. */
  event: string;
  /** Small, PII-free payload: ids, names, statuses, numbers. */
  data: Record<string, unknown>;
  /** Supabase access token of the current session (Bearer auth). */
  accessToken: string;
  /** Gateway base URL override; defaults to NEXT_PUBLIC_EVENTS_URL or the public host. */
  eventsUrl?: string;
}

function readEnvEventsUrl(): string | undefined {
  // NEXT_PUBLIC_* vars are inlined by Next.js at build time; guard for
  // non-Node runtimes where `process` is not defined at all.
  if (typeof process === 'undefined') return undefined;
  return process.env.NEXT_PUBLIC_EVENTS_URL || undefined;
}

/**
 * Resolve the gateway base URL for HTTP publishing.
 * Accepts ws(s):// values (a natural copy-paste from the Socket.io config)
 * and normalizes them to http(s)://, trimming any trailing slash.
 */
export function resolveEventsUrl(eventsUrl?: string): string {
  const base = eventsUrl || readEnvEventsUrl() || DEFAULT_EVENTS_URL;
  return base.replace(/^ws(s?):\/\//i, 'http$1://').replace(/\/+$/, '');
}

/**
 * Publish a cross-module event via the websocket-gateway HTTP endpoint.
 *
 * POST {eventsUrl}/api/events/publish
 * Authorization: Bearer <supabase access token>
 * Body: { module, event, data }
 *
 * Never throws — logs a console.warn and returns false on any failure so
 * callers can treat it as fire-and-forget.
 */
export async function publishModuleEvent(opts: PublishModuleEventOptions): Promise<boolean> {
  const { module: moduleName, event, data, accessToken, eventsUrl } = opts;

  if (!moduleName || !event) {
    console.warn('[module-events] Missing module or event name, skipping publish', {
      module: moduleName,
      event,
    });
    return false;
  }

  if (!accessToken) {
    console.warn(`[module-events] No access token, skipping publish of ${moduleName}.${event}`);
    return false;
  }

  const url = `${resolveEventsUrl(eventsUrl)}/api/events/publish`;

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ module: moduleName, event, data: data ?? {} }),
    });

    if (!res.ok) {
      console.warn(`[module-events] Failed to publish ${moduleName}.${event}: HTTP ${res.status}`);
      return false;
    }

    return true;
  } catch (error) {
    console.warn(`[module-events] Failed to publish ${moduleName}.${event}:`, error);
    return false;
  }
}

/**
 * Minimal structural type for a Supabase client — each module keeps its own
 * client instance; this package must not depend on @supabase/supabase-js.
 */
export interface SupabaseLikeClient {
  auth: {
    getSession(): Promise<{
      data: { session: { access_token?: string | null } | null };
    }>;
  };
}

/**
 * Read the current session's access token from a module's own Supabase client.
 * Returns null (and warns) instead of throwing when there is no session.
 */
export async function getSupabaseAccessToken(supabase: SupabaseLikeClient): Promise<string | null> {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.access_token ?? null;
  } catch (error) {
    console.warn('[module-events] Failed to read Supabase session:', error);
    return null;
  }
}
